import { InputRule, inputRules } from 'prosemirror-inputrules';

/**
 * Regras de digitacao com a semantica do WhatsApp.
 *
 * A diferenca em relacao ao CommonMark esta no asterisco simples: aqui `*x*`
 * aplica NEGRITO, nao italico, porque e assim que o usuario que passa o dia no
 * WhatsApp espera. O italico fica no underline, `_x_`.
 *
 * Isso muda apenas o que o usuario digita e ve. O que sai do editor continua
 * sendo CommonMark (`**negrito**` / `*italico*`) — a marcacao digitada e
 * consumida e vira mark de verdade no documento.
 */

/**
 * Monta uma regra que aplica um mark ao texto entre delimitadores.
 *
 * O regex precisa expor dois grupos: o prefixo (o caractere que antecede o
 * delimitador de abertura, ou vazio no inicio da linha) e o conteudo. O prefixo
 * existe para localizar o conteudo com precisao — procurar o texto dentro do
 * match por indexOf erraria em casos como `a*a*`.
 *
 * @param {RegExp} regexp precisa terminar em `$` e capturar (prefixo)(conteudo)
 * @param {MarkType} markType mark a aplicar
 * @param {number} delimiterLength tamanho do delimitador de abertura
 * @returns {InputRule}
 */
function markInputRule(regexp, markType, delimiterLength) {
  return new InputRule(regexp, (state, match, start, end) => {
    const [, prefix = '', content] = match;

    if (!content) {
      return null;
    }

    const contentStart = start + prefix.length + delimiterLength;
    const contentEnd = contentStart + content.length;

    // Dentro de `codigo` nada e marcacao — `*` ali e um asterisco literal.
    const codeMark = state.schema.marks.code;
    if (markType !== codeMark && codeMark) {
      if (codeMark.isInSet(state.doc.resolve(contentStart + 1).marks())) {
        return null;
      }

      // O mark de codigo so passa a existir quando a crase de fechamento e
      // digitada, entao checar o mark nao basta: enquanto o usuario digita
      // `a *b* c`, o `*b*` viraria negrito antes de o codigo se formar. Uma
      // crase em aberto no bloco indica que ainda estamos dentro de um trecho
      // de codigo, e a marcacao deve ser ignorada.
      const $start = state.doc.resolve(start);
      const textToCursor =
        $start.parent.textBetween(0, $start.parentOffset, null, '￼') +
        match[0];

      if ((textToCursor.match(/`/g) || []).length % 2 === 1) {
        return null;
      }
    }

    if (!state.doc.resolve(contentStart).parent.type.allowsMarkType(markType)) {
      return null;
    }

    const tr = state.tr;

    // Remove primeiro o delimitador de fechamento: apagar o de abertura antes
    // deslocaria as posicoes do fechamento.
    tr.delete(contentEnd, end);
    tr.delete(start + prefix.length, contentStart);

    const markStart = start + prefix.length;
    tr.addMark(markStart, markStart + content.length, markType.create());
    tr.removeStoredMark(markType);

    return tr;
  });
}

// Um delimitador nao pode encostar em espaco pelo lado de dentro: `* x *` e
// texto, `*x*` e marcacao. O conteudo tambem nao pode conter o proprio
// delimitador, senao `*a* e *b*` casaria como um bloco so.
const content = char => `([^${char}\\s][^${char}]*[^${char}\\s]|[^${char}\\s])`;

export const STRONG_DOUBLE_ASTERISK = new RegExp(
  `(^|[^*])\\*\\*${content('*')}\\*\\*$`
);
export const STRONG_SINGLE_ASTERISK = new RegExp(
  `(^|[^*\\w])\\*${content('*')}\\*$`
);
export const EM_UNDERSCORE = new RegExp(`(^|[^_\\w])_${content('_')}_$`);
export const STRIKE_DOUBLE_TILDE = new RegExp(
  `(^|[^~])~~${content('~')}~~$`
);
export const STRIKE_SINGLE_TILDE = new RegExp(`(^|[^~])~${content('~')}~$`);
export const CODE_BACKTICK = new RegExp('(^|[^`])`([^`\\s][^`]*|[^`\\s])`$');

/**
 * @param {Schema} schema
 * @returns {Plugin}
 */
export function whatsappInputRules(schema) {
  const rules = [];

  if (schema.marks.strong) {
    // `**x**` primeiro: com a regra de asterisco simples na frente, o par
    // duplo nunca chegaria a ser avaliado.
    rules.push(
      markInputRule(STRONG_DOUBLE_ASTERISK, schema.marks.strong, 2),
      markInputRule(STRONG_SINGLE_ASTERISK, schema.marks.strong, 1)
    );
  }

  if (schema.marks.em) {
    rules.push(markInputRule(EM_UNDERSCORE, schema.marks.em, 1));
  }

  if (schema.marks.strike) {
    rules.push(
      markInputRule(STRIKE_DOUBLE_TILDE, schema.marks.strike, 2),
      markInputRule(STRIKE_SINGLE_TILDE, schema.marks.strike, 1)
    );
  }

  if (schema.marks.code) {
    rules.push(markInputRule(CODE_BACKTICK, schema.marks.code, 1));
  }

  return inputRules({ rules });
}

export default whatsappInputRules;
